import React, { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  ICON_TYPES,
} from '../../../shared/constants';
import { ContentfulLayout } from '../../../shared/constants/Contentful.constants';

import CloseIcon from '../../atoms/icons/CloseIcon';
import Paper from '../../atoms/paper/Paper';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';

import { articleTrackPageView, DEQ_PAGE_TYPES } from '../../../utils/deq';
import { useHomepageStaticData } from '../../../utils/generic/homepageStaticQuery';
import { renderBodyItem } from '../../../utils/generic/renderBodyItem';
import SEO from '../../utils/SEO';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import { StyledContent } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { getRecaptchaSiteKey } from '../../../services/re-captcha.service';
import { ArticleVariants } from '../../../tokens';
import ArticleLayout from '../../layouts/article-layout/ArticleLayout';
import BaseLayout from '../../layouts/BaseLayout';
import DeprecatedArticleLayout from '../../layouts/deprecated/DeprecatedArticleLayout';
import ContentWrapper from '../../molecules/content-wrapper/ContentWrapper';

import { parseArticleTags, parsePageData } from './ArticlePage.helpers';
import { ArticlePageProps } from './ArticlePage.types';

const ARTICLE_QUERY = gql`
  query ArticleCollection($articleId: String!) {
    article(id: $articleId) {
      title
      mainImage {
        url
        width
        height
        description
      }
      tagsCollection {
        items {
          _id
          name
          value
          type
        }
      }
      publishingTime
      lastContentUpdate
      readingMinutes
      articleBodyCollection {
        items {
          ... on ArticleParagraph {
            _id
            name
            variant
            header
            body
            image {
              url
              width
              height
              description
            }
          }
        }
      }
    }
  }
`;

const hasWindow = typeof window !== 'undefined';
const csrEnabled = !!process.env.GATSBY_CSR_ENABLED;
const csrQueryEnabled = csrEnabled && hasWindow;

const ArticlePage = ({ data, pageContext }: ArticlePageProps) => {
  const pageData = data.contentfulPageArticle;
  const { intakeCard } = useHomepageStaticData();
  const [tags, allTagNames] = parseArticleTags(pageData);
  const [csrDataFetched, setCsrDataFetched] = useState(false);
  const [csrDialogOpened, setCsrDialogOpened] = useState(true);

  // createdAt and updatedAt are default fields that will have a value
  // the other 2 are added later on and some articles don't have them
  const pubDate = pageData?.publishingTime ?? pageData.createdAt;
  const modDate = pageData?.lastContentUpdate ?? pageData.updatedAt;
  const readTime = `${(pageData?.readingMinutes ?? 0) * 60}`;
  const pageTitle = `${pageData?.subHeader} | ${pageData?.title}`.toLowerCase();

  useEffect(() => {
    articleTrackPageView(
      pageData.authorRef?.name,
      allTagNames as string[],
      pageTitle,
      pubDate,
      modDate,
      readTime
    );
  }, []);

  // TODO: Implement loading state?
  const {
    loading,
    error,
    data: queryData,
    refetch,
  } = useQuery(ARTICLE_QUERY, {
    variables: { articleId: pageContext.contentfulId },
    fetchPolicy: 'network-only',
    skip: !csrDataFetched,
  });

  const parsedPageData = queryData
    ? parsePageData(pageData, queryData)
    : pageData;

  const handleFetchData = () => {
    if (!csrDataFetched) {
      setCsrDataFetched(true);
    } else {
      refetch();
    }
  };

  const handleCloseCsrDialog = () => {
    setCsrDialogOpened(false);
  };

  return (
    <BaseLayout layoutType={ContentfulLayout.DEFAULT}>
      <SEO
        title={parsedPageData.metadataTitle}
        description={parsedPageData.metadataDescription?.metadataDescription}
        lang={parsedPageData.metadataLocale}
        canonical={parsedPageData.metadataCanonical}
        image={
          parsedPageData.metadataImage.gatsbyImageData?.images?.fallback?.src
        }
      />
      {parsedPageData.variant === ArticleVariants.DEFAULT ? (
        <>
          <ArticleLayout
            dateTime={{
              publishedAt: parsedPageData.publishingTime,
              updatedAt: parsedPageData.lastContentUpdate,
              readingTime: parsedPageData.readingMinutes,
            }}
            header={{ image: parsedPageData.mainImage }}
            title={parsedPageData.title}
            tags={parsedPageData.tags}
            body={parsedPageData.articleBody}
          />
          {parsedPageData.body?.length && (
            <ContentWrapper>
              <StyledContent>
                {parsedPageData.body.map((item, key) =>
                  renderBodyItem(
                    item,
                    key,
                    DEQ_PAGE_TYPES.article,
                    parsedPageData.tags,
                    parsedPageData.slug,
                    parsedPageData.parentPage,
                    parsedPageData.title
                  )
                )}
              </StyledContent>
            </ContentWrapper>
          )}
          {csrQueryEnabled && csrDialogOpened && (
            <StickyReload
              fetch={handleFetchData}
              close={handleCloseCsrDialog}
              loading={loading}
              error={!!error}
            />
          )}
        </>
      ) : (
        <DeprecatedArticleLayout
          intakeCard={intakeCard}
          articlesData={pageContext.articles}
          data={parsedPageData}
          tags={tags}
        />
      )}
    </BaseLayout>
  );
};

interface StickyReloadProps {
  fetch: () => void;
  close: () => void;
  loading: boolean;
  error: boolean;
}

const StickyReload = ({ fetch, close, loading, error }: StickyReloadProps) => (
  <StyledStickyReload>
    <StyckyPaper>
      <StyledButton type="button" onClick={close}>
        <CloseIcon color={colors.black} width="16" height="16" />
      </StyledButton>
      <PrimaryButton
        disabled={loading || error}
        type={BUTTON_TYPES.highlight}
        buttonTheme={BUTTON_THEMES.highlight}
        label={!loading ? 'Magic Refresh' : 'Refreshing...'}
        icon={ICON_TYPES.rocket}
        onButtonClick={fetch}
      />
    </StyckyPaper>
  </StyledStickyReload>
);

const StyledButton = styled.button`
  background: ${colors.white};
  border: none;
  border-radius: 100%;
  cursor: pointer;
  font-size: 0;
  margin: 0;
  outline: 0;
  overflow: hidden;
  padding: ${Spacings.XS};
  position: absolute;
  right: -16px;
  top: -14px;
`;

const StyckyPaper = styled(Paper)`
  padding: ${Spacings.M};
  position: relative;

  ${mq.from.breakpoint.M`
    padding: ${Spacings.M};
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.M};
  `}

  ${mq.from.breakpoint.XL`
    padding: ${Spacings.M};
  `}
`;

const StyledStickyReload = styled.div`
  bottom: ${Spacings.M};
  position: fixed;
  right: ${Spacings.M};
`;

export const data = graphql`
  query ArticlesQuery($slug: String) {
    contentfulPageArticle(slug: { eq: $slug }) {
      slug
      title
      variant
      titleForCarousel
      subHeader
      ...ParentPageArticle
      authorRef {
        name
        url
      }
      publishingTime(formatString: "YYYY-MM-DD")
      createdAt(formatString: "YYYY-MM-DD")
      updatedAt(formatString: "YYYY-MM-DD")
      lastContentUpdate
      readingMinutes
      tags {
        id
        name
        value
        type
      }
      relatedProductTile {
        ...Card
      }
      relatedProductCard {
        ...Card
      }
      mainImage {
        gatsbyImageData(layout: FULL_WIDTH)
      }
      metadataTitle
      metadataDescription {
        metadataDescription
      }
      metadataImage {
        gatsbyImageData(layout: FULL_WIDTH)
      }
      metadataLocale
      metadataCanonical
      lead {
        id
        lead
      }
      leadBody {
        childMarkdownRemark {
          rawMarkdownBody
        }
      }
      articleType
      articleBody {
        ... on ContentfulArticleTextSection {
          __typename
          id
          name
          variant
          header
          paragraphText {
            raw
            references {
              ... on ContentfulAsset {
                contentful_id
                __typename
                gatsbyImageData(layout: FULL_WIDTH, quality: 100)
              }
              ...Card
            }
          }
          body {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          links {
            ...Link
          }
          image {
            gatsbyImageData(layout: FULL_WIDTH)
            description
            file {
              url
            }
          }
        }
        ...Card
      }
      body {
        ... on ContentfulCompCard {
          ...Card
        }
        ... on ContentfulCompCardWrapper {
          ...CardWrapper
        }
      }
      feedbackCard {
        ...CardWrapper
      }
    }
  }
`;

export default ArticlePage;

export function Head() {
  return (
    <script
      async
      src={`https://www.google.com/recaptcha/api.js?render=${getRecaptchaSiteKey()}`}
    />
  );
}
